// Servicio de autenticación para la app PWA
// Basado en auth.ts de la app WEB

export interface User {
  id?: number;
  username?: string;
  email?: string;
  nombre_completo?: string;
  role?: string;
  permissions?: string[];
  home_page?: string | null;
  home_tab?: number | null;
  home_room?: number | null;
  can_view_cameras?: boolean;  // Permiso para ver cámaras
  can_use_ai?: boolean;  // Permiso para usar el Asistente IA
  can_view_music?: boolean;  // Permiso para ver la página Audio (Sonos)
  can_view_ac?: boolean;
  can_view_tv?: boolean;
  can_view_android_tv?: boolean;
  can_view_heating?: boolean;
  can_view_pileta?: boolean;
  can_view_solar?: boolean;
  can_answer_doorbell?: boolean;
  can_use_phone?: boolean;
  softphone_sip_extension?: string | null;
  has_softphone_sip_password?: boolean;
}

// Función para verificar permisos
export const checkPermission = (user: User, permission: string): boolean => {
  if (!user || !user.permissions) {
    return false;
  }
  
  // Si es admin, tiene todos los permisos
  if (user.role === 'admin' || user.permissions.includes('admin')) {
    return true;
  }
  
  // Verificar permiso específico
  return user.permissions.includes(permission);
};

// Función para verificar si es admin
export const isAdmin = (user: User): boolean => {
  return user?.role === 'admin' || user?.permissions?.includes('admin') || false;
};

// Función para verificar si es demo
export const isDemo = (user: User): boolean => {
  return user?.role === 'demo' || false;
};

// Función para verificar si es demo o admin (para verificaciones de vista)
export const isDemoOrAdmin = (user: User): boolean => {
  return isAdmin(user) || isDemo(user);
};

// Función para verificar si el usuario puede ver cámaras (opt-in explícito).
// Usuarios demo nunca tienen acceso global; la excepción Calle vive en CameraTab.
export const canViewCameras = (user: User | null): boolean => {
  if (!user) return false;
  if (isDemo(user)) return false;
  if (isAdmin(user)) return true;
  return user.can_view_cameras === true;
};

// Función para verificar si el usuario puede usar el Asistente IA (opt-in explícito)
export const canUseAI = (user: User | null): boolean => {
  if (!user) return false;
  if (isAdmin(user)) return true;
  return user.can_use_ai === true;
};

// Ver Audio (página Music + tarjetas Sonos en habitaciones/favoritos).
// Admin siempre; resto según flag can_view_music (mismo patrón que AC/TV).
export const canViewMusic = (user: User | null): boolean => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  return Boolean(user.can_view_music);
};

export const canViewAc = (user: User | null): boolean => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  return Boolean(user.can_view_ac);
};

export const canViewTv = (user: User | null): boolean => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  return Boolean(user.can_view_tv);
};

export const canViewAndroidTv = (user: User | null): boolean => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  return Boolean(user.can_view_android_tv);
};

export const canViewHeating = (user: User | null): boolean => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  return Boolean(user.can_view_heating);
};

export const canViewPileta = (user: User | null): boolean => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  return Boolean(user.can_view_pileta);
};

/** Si la API no manda la clave, la columna todavía no existe y solar sigue visible. */
export const canViewSolar = (user: User | null): boolean => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  if (user.can_view_solar === undefined || user.can_view_solar === null) return true;
  return user.can_view_solar === true;
};

/** SoftPhone general (teclado / contactos / recientes). Admin siempre. */
export const canUsePhone = (user: User | null): boolean => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  return Boolean(user.can_use_phone);
};

/** Atender timbre (flujo Video fullscreen). Admin siempre. */
export const canAnswerDoorbell = (user: User | null): boolean => {
  if (!user) return false;
  if (user.role === 'admin') return true;
  return Boolean(user.can_answer_doorbell);
};

// Función para obtener token del localStorage
export const getToken = (): string | null => {
  return localStorage.getItem('token');
};

// Función para guardar token en localStorage
export const setToken = (token: string): void => {
  localStorage.setItem('token', token);
  import('./pushAuthStorage').then(({ savePushAuthToken }) => {
    void savePushAuthToken(token);
  }).catch(() => { /* noop */ });
};

// Función para eliminar token del localStorage
export const removeToken = (): void => {
  localStorage.removeItem('token');
  import('./pushAuthStorage').then(({ savePushAuthToken }) => {
    void savePushAuthToken(null);
  }).catch(() => { /* noop */ });
};

// Función para verificar si el usuario está autenticado
export const isAuthenticated = (): boolean => {
  const token = getToken();
  return token !== null && token !== '';
};

// Función para obtener usuario del localStorage
export const getUser = (): User | null => {
  const userStr = localStorage.getItem('user');
  if (userStr) {
    try {
      return JSON.parse(userStr);
    } catch (error) {
      console.error('Error parsing user from localStorage:', error);
      return null;
    }
  }
  return null;
};

// Función para guardar usuario en localStorage
export const setUser = (user: User): void => {
  localStorage.setItem('user', JSON.stringify(user));
};

// Función para eliminar usuario del localStorage
export const removeUser = (): void => {
  localStorage.removeItem('user');
};

// Función para logout
export const logout = (): void => {
  removeToken();
  removeUser();
  localStorage.removeItem('shcMcpToken');
  localStorage.removeItem('shcMcpTokenBinding');
};
